/**
 * Resolve a human city label for restaurant SEO (titles, JSON-LD, visible HTML).
 * Never surfaces the placeholder slug "other" / label "Other".
 */

const SLUG_LABELS: Record<string, string> = {
  barcelona: "Barcelona",
  madrid: "Madrid",
  lisbon: "Lisboa",
  "a-coruna": "A Coruña",
  bilbao: "Bilbao",
  oviedo: "Oviedo",
  gijon: "Gijón",
  valencia: "Valencia",
  vigo: "Vigo",
};

const COUNTRY_OR_REGION =
  /^(españa|spain|ee\.?\s*uu\.?|usa|united states|portugal|france|francia|asturias|galicia|catalunya|cataluña|catalonia|california|ca)$/i;

function titleCaseSlug(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Label for a stored city_slug; null when slug is missing or the "other" placeholder. */
export function labelFromCitySlug(slug: string | null | undefined): string | null {
  if (!slug || typeof slug !== "string") return null;
  const normalized = slug.trim().toLowerCase();
  if (!normalized || normalized === "other") return null;
  return SLUG_LABELS[normalized] ?? titleCaseSlug(normalized);
}

/**
 * Best-effort city name from a free-form address (Google-style formatted strings).
 * Examples:
 *  - "20620 Homestead Rd, Cupertino, CA 95014, EE. UU." → "Cupertino"
 *  - "Rúa Comandante Fontanes, 17, 15003 A Coruña" → "A Coruña"
 */
export function cityNameFromAddress(address: string | null | undefined): string | null {
  if (!address || typeof address !== "string") return null;
  const parts = address
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2) return null;

  let end = parts.length - 1;
  while (end >= 1 && COUNTRY_OR_REGION.test(parts[end])) {
    end -= 1;
  }

  for (let i = end; i >= 1; i--) {
    const part = parts[i];
    // "15003 A Coruña" / "08001 Barcelona"
    const postalCity = part.match(/^\d{4,5}\s+(.+)$/);
    if (postalCity) {
      const city = postalCity[1].trim();
      if (city && !COUNTRY_OR_REGION.test(city)) return city;
    }
    // "CA 95014" style — skip
    if (/^[A-Z]{2}\s+\d{4,5}/i.test(part)) continue;
    if (/^\d{4,5}$/.test(part)) continue;
    if (COUNTRY_OR_REGION.test(part)) continue;
    // Plain locality token
    if (/^[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s'.-]{1,48}$/.test(part)) {
      return part;
    }
  }
  return null;
}

export type RestaurantCityInput = {
  city_slug?: string | null;
  address_text?: string | null;
  /** Optional pre-resolved slug from coords (e.g. resolve_city_slug_from_point). */
  resolved_city_slug?: string | null;
};

/**
 * Display city for SEO: real location label, never "Other".
 * Precedence: meaningful city_slug → coord-resolved slug → address parse.
 */
export function resolveRestaurantCityLabel(input: RestaurantCityInput): string | null {
  return (
    labelFromCitySlug(input.city_slug) ||
    labelFromCitySlug(input.resolved_city_slug) ||
    cityNameFromAddress(input.address_text)
  );
}
